import React, { useState, useEffect } from "react";
import { updatePatientProfile } from "../../api/apiService";
import {
  validatePatientField,
  deepSanitizePayload,
} from "../../utils/inputValidation";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const EditPatientModal = ({ isOpen, onClose, patientData, onUpdateSuccess }) => {
  const { can, user } = useAuth();
  const canUpdatePatient = can(PERMISSIONS.PATIENTS_UPDATE);
  const [formData, setFormData] = useState({
    f_name: "",
    m_name: "",
    l_name: "",
    mobile: "",
    dob: "",
    gender: "",
    address: "",
    email: "",
    allergies: "",
    insuranceProvider: "",
    policyNumber: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Check if patient is ABHA verified
  const isAbhaVerified = !!(patientData?.abhaAddress || patientData?.abhaNumber || patientData?.ABHANumber);

  const normalizeGender = (g) => {
    if (!g) return "";
    const gender = String(g).toUpperCase();
    if (gender === "M" || gender === "MALE") return "Male";
    if (gender === "F" || gender === "FEMALE") return "Female";
    if (gender === "O" || gender === "OTHER") return "Other";
    return g;
  };

  const formatDob = (d) => {
    if (!d) return "";
    const s = String(d).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const dmyMatch = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmyMatch) {
      return `${dmyMatch[3]}-${dmyMatch[2].padStart(2, "0")}-${dmyMatch[1].padStart(2, "0")}`;
    }
    if (s.includes("T")) return s.split("T")[0];
    try {
      const date = new Date(s);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split("T")[0];
      }
    } catch (e) { }
    return s;
  };

  useEffect(() => {
    if (isOpen && patientData) {
      const rawFullName = patientData.name || patientData.fullName || "";
      const nameParts = rawFullName.split(/\s+/).filter(Boolean);

      const rawFname = patientData.f_name || patientData.firstName || nameParts[0] || "";
      const rawMname = patientData.m_name || patientData.middleName || (nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "") || "";
      const rawLname = patientData.l_name || patientData.lastName || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : "") || "";
      const rawMobile = patientData.mobile || patientData.phone || patientData.mobileNumber || "";

      setFormData({
        f_name: rawFname,
        m_name: rawMname,
        l_name: rawLname,
        mobile: rawMobile,
        dob: formatDob(patientData.dob || patientData.dateOfBirth || patientData.date_of_birth || ""),
        gender: normalizeGender(patientData.gender || patientData.Gender || patientData.sex || patientData.Sex || ""),
        address: patientData.address || "",
        email: patientData.email || "",
        allergies: patientData.allergies || "",
        insuranceProvider: patientData.insurance?.provider || patientData.insuranceProvider || "",
        policyNumber: patientData.insurance?.policyNumber || patientData.policyNumber || "",
      });
      setFieldErrors({});
      setError("");
    }
  }, [isOpen, patientData]);

  // Real-time validation without keystroke auto-correction
  const handleChange = (e) => {
    const { name, value } = e.target;
    const safeVal = typeof value === "string" ? value : String(value || "");
    setFormData((prev) => ({ ...prev, [name]: safeVal }));

    const isReq = ["f_name", "l_name", "mobile", "gender"].includes(name);
    const fieldErr = validatePatientField(name, safeVal, isReq);
    setFieldErrors((prev) => ({ ...prev, [name]: fieldErr }));

    if (fieldErr) {
      setError(fieldErr);
    } else {
      setError("");
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const isReq = ["f_name", "l_name", "mobile", "gender"].includes(name);
    const fieldErr = validatePatientField(name, value, isReq);
    setFieldErrors((prev) => ({ ...prev, [name]: fieldErr }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Anti-tamper check: ensure no active errors are bypassed via force-trigger
    const activeErrors = Object.values(fieldErrors).filter(Boolean);
    if (activeErrors.length > 0) {
      setError(activeErrors[0]);
      return;
    }

    // Comprehensive validation pass on all fields
    const newFieldErrors = {};
    const requiredFields = ["f_name", "l_name", "mobile", "gender"];
    requiredFields.forEach((f) => {
      const err = validatePatientField(f, formData[f], true);
      if (err) newFieldErrors[f] = err;
    });

    const optionalFields = [
      "m_name",
      "dob",
      "email",
      "address",
      "insuranceProvider",
      "policyNumber",
      "allergies",
    ];
    optionalFields.forEach((f) => {
      const err = validatePatientField(f, formData[f], false);
      if (err) newFieldErrors[f] = err;
    });

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError(Object.values(newFieldErrors)[0]);
      return;
    }

    if (!canUpdatePatient) {
      setError("You do not have permission to update patient profiles.");
      return;
    }

    setLoading(true);

    try {
      const patientId = patientData._id || patientData.id || patientData.uhid;

      // Structure clean payload
      const payload = {
        f_name: formData.f_name.trim(),
        m_name: formData.m_name.trim(),
        l_name: formData.l_name.trim(),
        mobile: formData.mobile.trim(),
        dob: formData.dob,
        gender: formData.gender,
        address: formData.address.trim(),
        email: formData.email.trim(),
        allergies: formData.allergies.trim(),
        insurance: {
          provider: formData.insuranceProvider.trim(),
          policyNumber: formData.policyNumber.trim(),
        },
      };

      const response = await updatePatientProfile(patientId, deepSanitizePayload(payload));
      if (onUpdateSuccess) {
        onUpdateSuccess(response.data || response);
      }
      onClose();
    } catch (err) {
      console.error("Error updating patient profile:", err);
      setError(
        err?.response?.data?.message || err.message || "Failed to update profile.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isLocked = (fieldName) => {
    // Requirements: Name and DOB should not be editable if patient has ABHA.
    if (!isAbhaVerified) return false;
    const lockedFields = ["f_name", "m_name", "l_name", "dob", "gender"];
    return lockedFields.includes(fieldName);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1050,
        backgroundColor: "rgba(17, 24, 39, 0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        scrollbarWidth: "none",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          width: "550px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflow: "auto",
          scrollbarWidth: "none",

          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
      >
        <div style={{ padding: "24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #F3F4F6", position: "sticky", top: "0", zIndex: 10, backgroundColor: "#fff" }}>
          <h5 style={{ margin: 0, color: "#1E3A8A", fontWeight: 700, fontSize: "20px" }}>Edit Patient Profile</h5>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "24px" }}>
          {!canUpdatePatient && (
            <div
              style={{
                background: "#EBF5FF",
                border: "1px solid #B4C6FC",
                borderRadius: "8px",
                padding: "10px 14px",
                marginBottom: "16px",
                color: "#1E429F",
                fontSize: "13px",
              }}
            >
              <strong>Read-Only Mode:</strong> You do not have permission to edit patient profiles.
            </div>
          )}

          {error && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px", color: "#DC2626", fontSize: "13px" }}>
              {error}
            </div>
          )}

          <fieldset
            disabled={!canUpdatePatient}
            style={{
              border: "none",
              padding: 0,
              margin: 0,
              pointerEvents: !canUpdatePatient ? "none" : "auto",
              opacity: !canUpdatePatient ? 0.88 : 1,
            }}
          >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
                First Name *
              </label>
              <input
                type="text"
                name="f_name"
                value={formData.f_name}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLocked("f_name")}
                placeholder="First Name"
                className={fieldErrors.f_name ? "is-invalid" : ""}
                aria-invalid={fieldErrors.f_name ? "true" : "false"}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: fieldErrors.f_name ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                  borderRadius: "8px",
                  fontSize: "14px",
                  backgroundColor: isLocked("f_name") ? "#F3F4F6" : "#fff",
                }}
              />
              {fieldErrors.f_name && (
                <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {fieldErrors.f_name}
                </span>
              )}
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
                Middle Name
              </label>
              <input
                type="text"
                name="m_name"
                value={formData.m_name}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLocked("m_name")}
                placeholder="Middle Name"
                className={fieldErrors.m_name ? "is-invalid" : ""}
                aria-invalid={fieldErrors.m_name ? "true" : "false"}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: fieldErrors.m_name ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                  borderRadius: "8px",
                  fontSize: "14px",
                  backgroundColor: isLocked("m_name") ? "#F3F4F6" : "#fff",
                }}
              />
              {fieldErrors.m_name && (
                <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {fieldErrors.m_name}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
                Last Name *
              </label>
              <input
                type="text"
                name="l_name"
                value={formData.l_name}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLocked("l_name")}
                placeholder="Last Name"
                className={fieldErrors.l_name ? "is-invalid" : ""}
                aria-invalid={fieldErrors.l_name ? "true" : "false"}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: fieldErrors.l_name ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                  borderRadius: "8px",
                  fontSize: "14px",
                  backgroundColor: isLocked("l_name") ? "#F3F4F6" : "#fff",
                }}
              />
              {fieldErrors.l_name && (
                <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {fieldErrors.l_name}
                </span>
              )}
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
                Mobile Number *
              </label>
              <input
                type="tel"
                name="mobile"
                maxLength={10}
                value={formData.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="10-digit mobile number"
                className={fieldErrors.mobile ? "is-invalid" : ""}
                aria-invalid={fieldErrors.mobile ? "true" : "false"}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: fieldErrors.mobile ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
              {fieldErrors.mobile && (
                <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {fieldErrors.mobile}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
                Date of Birth
              </label>
              <input
                type="date"
                name="dob"
                max={new Date().toISOString().split("T")[0]}
                value={formData.dob}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLocked("dob")}
                className={fieldErrors.dob ? "is-invalid" : ""}
                aria-invalid={fieldErrors.dob ? "true" : "false"}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: fieldErrors.dob ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                  borderRadius: "8px",
                  fontSize: "14px",
                  backgroundColor: isLocked("dob") ? "#F3F4F6" : "#fff",
                }}
              />
              {fieldErrors.dob && (
                <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {fieldErrors.dob}
                </span>
              )}
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
                Gender *
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLocked("gender")}
                className={fieldErrors.gender ? "is-invalid" : ""}
                aria-invalid={fieldErrors.gender ? "true" : "false"}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: fieldErrors.gender ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                  borderRadius: "8px",
                  fontSize: "14px",
                  backgroundColor: isLocked("gender") ? "#F3F4F6" : "#fff",
                }}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {fieldErrors.gender && (
                <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {fieldErrors.gender}
                </span>
              )}
            </div>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g. name@example.com"
              className={fieldErrors.email ? "is-invalid" : ""}
              aria-invalid={fieldErrors.email ? "true" : "false"}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: fieldErrors.email ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
            {fieldErrors.email && (
              <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                {fieldErrors.email}
              </span>
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
              Current Address
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              onBlur={handleBlur}
              rows="2"
              placeholder="Address"
              className={fieldErrors.address ? "is-invalid" : ""}
              aria-invalid={fieldErrors.address ? "true" : "false"}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: fieldErrors.address ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                borderRadius: "8px",
                fontSize: "14px",
                resize: "none",
              }}
            ></textarea>
            {fieldErrors.address && (
              <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                {fieldErrors.address}
              </span>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
                Insurance Provider
              </label>
              <input
                type="text"
                name="insuranceProvider"
                value={formData.insuranceProvider}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Provider Name"
                className={fieldErrors.insuranceProvider ? "is-invalid" : ""}
                aria-invalid={fieldErrors.insuranceProvider ? "true" : "false"}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: fieldErrors.insuranceProvider ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
              {fieldErrors.insuranceProvider && (
                <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {fieldErrors.insuranceProvider}
                </span>
              )}
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
                Policy Number
              </label>
              <input
                type="text"
                name="policyNumber"
                value={formData.policyNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Policy #"
                className={fieldErrors.policyNumber ? "is-invalid" : ""}
                aria-invalid={fieldErrors.policyNumber ? "true" : "false"}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: fieldErrors.policyNumber ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
              {fieldErrors.policyNumber && (
                <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {fieldErrors.policyNumber}
                </span>
              )}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "6px", fontSize: "14px" }}>
              Allergies (comma separated)
            </label>
            <input
              type="text"
              name="allergies"
              value={formData.allergies}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.allergies ? "is-invalid" : ""}
              aria-invalid={fieldErrors.allergies ? "true" : "false"}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: fieldErrors.allergies ? "1.5px solid #EF4444" : "1px solid #D1D5DB",
                borderRadius: "8px",
                fontSize: "14px",
              }}
              placeholder="e.g. Peanuts, Penicillin"
            />
            {fieldErrors.allergies && (
              <span style={{ color: "#DC2626", fontSize: "12px", marginTop: "4px", display: "block" }}>
                {fieldErrors.allergies}
              </span>
            )}
          </div>
          </fieldset>

          <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                flex: 1,
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #E5E7EB",
                background: "#FFF",
                color: "#374151",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "15px",
              }}
            >
              {canUpdatePatient ? "Cancel" : "Close"}
            </button>
            {canUpdatePatient && (
              <button
                type="submit"
                disabled={loading || Object.values(fieldErrors).some(Boolean)}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "8px",
                  border: "none",
                  background: Object.values(fieldErrors).some(Boolean) ? "#93C5FD" : "#2563EB",
                  color: "#fff",
                  cursor: Object.values(fieldErrors).some(Boolean) ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  fontSize: "15px",
                }}
              >
                {loading ? "Updating..." : "Save Changes"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>

  );
};

export default EditPatientModal;
